import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { 
  ChartBarIcon, 
  ExclamationTriangleIcon, 
  ShieldCheckIcon, 
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  PlusIcon,
  ClockIcon,
  ExclamationCircleIcon,
  UserGroupIcon,
  CogIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  Squares2X2Icon,
  CalendarIcon,
  EyeIcon,
  ArrowPathIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline'
import { issuesApi, risksApi, controlsApi, actionsApi, jobsApi, incidentsApi, kriApi, dashboardApi } from '../services/api'

// Dashboard component with tabbed interface
function Dashboard() {
  const [activeView, setActiveView] = useState('my') // 'my', 'team', 'all'
  const [activeTab, setActiveTab] = useState('dashboard') // 'dashboard', 'issues', 'incidents', 'actions', 'risks', 'controls', 'compliance', 'jobs'

  // Single summary call for tile counts — replaces 7 separate API calls
  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardApi.getSummary(),
  })

  // Full entity data — only fetched when the corresponding tab is active
  const { data: issuesData, isLoading: issuesLoading, refetch: refetchIssues } = useQuery({
    queryKey: ['dashboard-issues'],
    queryFn: () => issuesApi.getIssues({ limit: 1000 }),
    enabled: activeTab === 'issues',
  })

  const { data: risksData, isLoading: risksLoading, refetch: refetchRisks } = useQuery({
    queryKey: ['dashboard-risks'],
    queryFn: () => risksApi.getRisks({ limit: 1000 }),
    enabled: activeTab === 'risks',
  })

  const { data: controlsData, isLoading: controlsLoading, refetch: refetchControls } = useQuery({
    queryKey: ['dashboard-controls'],
    queryFn: () => controlsApi.getControls({ limit: 1000 }),
    enabled: activeTab === 'controls',
  })

  const { data: actionsData, isLoading: actionsLoading, refetch: refetchActions } = useQuery({
    queryKey: ['dashboard-actions'],
    queryFn: () => actionsApi.getActions({ limit: 1000 }),
    enabled: activeTab === 'actions',
  })

  const { data: jobsData, isLoading: jobsLoading, refetch: refetchJobs } = useQuery({
    queryKey: ['dashboard-jobs'],
    queryFn: () => jobsApi.getJobs({ limit: 1000 }),
    enabled: activeTab === 'jobs',
  })

  // Extract arrays from API responses for tab views (only populated when tab is active)
  const issues = Array.isArray(issuesData?.issues) ? issuesData.issues :
                 Array.isArray(issuesData?.items) ? issuesData.items :
                 Array.isArray(issuesData) ? issuesData : []

  const risks = Array.isArray(risksData?.risks) ? risksData.risks :
                Array.isArray(risksData?.items) ? risksData.items :
                Array.isArray(risksData) ? risksData : []

  const controls = Array.isArray(controlsData?.controls) ? controlsData.controls :
                   Array.isArray(controlsData?.items) ? controlsData.items :
                   Array.isArray(controlsData) ? controlsData : []

  const actions = Array.isArray(actionsData?.actions) ? actionsData.actions :
                  Array.isArray(actionsData?.items) ? actionsData.items :
                  Array.isArray(actionsData) ? actionsData : []

  const jobs = Array.isArray(jobsData?.jobs) ? jobsData.jobs :
               Array.isArray(jobsData?.items) ? jobsData.items :
               Array.isArray(jobsData) ? jobsData : []

  const refreshAllData = () => { refetchSummary() }

  // Stats from pre-aggregated summary (no client-side filtering of 1000s of rows)
  const s = summary || {}

  const issueStats = {
    total: s.issues?.total || 0,
    open: s.issues?.open || 0,
    closed: s.issues?.closed || 0,
    critical: 0, high: 0, medium: 0, low: 0,
  }

  const riskStats = {
    total: s.risks?.total || 0,
    high: s.risks?.high_critical || 0,
    medium: s.risks?.medium || 0,
    low: s.risks?.low || 0,
  }

  const controlStats = {
    total: s.controls?.total || 0,
    effective: s.controls?.effective || 0,
    needsReview: s.controls?.needs_review || 0,
    automated: s.controls?.automated || 0,
  }

  const actionStats = {
    total: s.actions?.total || 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
  }

  const jobStats = {
    total: s.jobs?.total || 0,
    todo: s.jobs?.todo || 0,
    inProgress: s.jobs?.in_progress || 0,
    completed: s.jobs?.completed || 0,
    scheduled: 0,
    overdue: 0,
  }

  const incidentStats = {
    total: s.incidents?.total || 0,
    open: s.incidents?.open || 0,
    investigating: s.incidents?.investigating || 0,
    resolved: s.incidents?.resolved || 0,
  }

  const kriStats = {
    total: s.kris?.total || 0,
    red: s.kris?.red || 0,
    amber: s.kris?.amber || 0,
    green: s.kris?.green || 0,
  }

  const complianceScore = s.compliance?.score || 0
  const complianceFrameworks = s.compliance?.frameworks || 0
  const complianceRequirements = s.compliance?.requirements || 0
  const complianceMetRequirements = s.compliance?.met || 0

  // Organized tiles in rows as requested: Risks/Incidents, Issues/Actions, Jobs/Controls, Compliance
  const tileRows = [
    // Row 1: Risks & Incidents
    [
      {
        title: 'Risks',
        icon: ShieldCheckIcon,
        color: 'border-red-200 bg-red-50',
        iconColor: 'text-red-600',
        addButton: 'Risk',
        addLink: '/risk-details/new',
        viewAllLink: '/risk-library',
        stats: [
          { label: 'Total Risks', value: riskStats.total, color: 'text-gray-900' },
          { label: 'High/Critical', value: riskStats.high, color: 'text-red-600' },
          { label: 'Medium', value: riskStats.medium, color: 'text-yellow-600' },
          { label: 'Low', value: riskStats.low, color: 'text-green-600' }
        ]
      },
      {
        title: 'Incidents',
        icon: ExclamationCircleIcon,
        color: 'border-purple-200 bg-purple-50',
        iconColor: 'text-purple-600',
        addButton: 'Incident',
        addLink: '/incident-library',
        viewAllLink: '/incident-library',
        stats: [
          { label: 'Total Incidents', value: incidentStats.total, color: 'text-gray-900' },
          { label: 'Open', value: incidentStats.open, color: 'text-red-600' },
          { label: 'Investigating', value: incidentStats.investigating, color: 'text-yellow-600' },
          { label: 'Resolved', value: incidentStats.resolved, color: 'text-green-600' }
        ]
      }
    ],
    // Row 2: Issues & Actions
    [
      {
        title: 'Issues',
        icon: ExclamationTriangleIcon,
        color: 'border-yellow-200 bg-yellow-50',
        iconColor: 'text-yellow-600',
        addButton: 'Issue',
        addLink: '/issue-details/new',
        viewAllLink: '/issue-library',
        stats: [
          { label: 'Total Issues', value: issueStats.total, color: 'text-gray-900' },
          { label: 'Open', value: issueStats.open, color: 'text-red-600' },
          { label: 'Critical/High', value: issueStats.critical + issueStats.high, color: 'text-red-600' },
          { label: 'Closed', value: issueStats.closed, color: 'text-green-600' }
        ]
      },
      {
        title: 'Actions',
        icon: ClipboardDocumentListIcon,
        color: 'border-blue-200 bg-blue-50',
        iconColor: 'text-blue-600',
        addButton: 'Action',
        addLink: '/action-library/new',
        viewAllLink: '/action-library',
        stats: [
          { label: 'Total Actions', value: actionStats.total, color: 'text-gray-900' },
          { label: 'Pending', value: actionStats.pending, color: 'text-yellow-600' },
          { label: 'In Progress', value: actionStats.inProgress, color: 'text-blue-600' },
          { label: 'Completed', value: actionStats.completed, color: 'text-green-600' }
        ]
      }
    ],
    // Row 3: Jobs & Controls
    [
      {
        title: 'Jobs',
        icon: BriefcaseIcon,
        color: 'border-indigo-200 bg-indigo-50',
        iconColor: 'text-indigo-600',
        addButton: 'Job',
        addLink: '/jobs/new',
        viewAllLink: '/jobs',
        stats: [
          { label: 'Total Jobs', value: jobStats.total, color: 'text-gray-900' },
          { label: 'To Do', value: jobStats.todo, color: 'text-gray-600' },
          { label: 'In Progress', value: jobStats.inProgress, color: 'text-blue-600' },
          { label: 'Completed', value: jobStats.completed, color: 'text-green-600' }
        ]
      },
      {
        title: 'Controls',
        icon: CheckCircleIcon,
        color: 'border-green-200 bg-green-50',
        iconColor: 'text-green-600',
        addButton: 'Control',
        addLink: '/control-library',
        viewAllLink: '/control-library',
        stats: [
          { label: 'Total Controls', value: controlStats.total, color: 'text-gray-900' },
          { label: 'Effective', value: controlStats.effective, color: 'text-green-600' },
          { label: 'Need Review', value: controlStats.needsReview, color: 'text-yellow-600' },
          { label: 'Automated', value: controlStats.automated, color: 'text-blue-600' }
        ]
      }
    ],
    // Row 4: KRIs & Compliance
    [
      {
        title: 'KRIs',
        icon: ChartBarIcon,
        color: 'border-teal-200 bg-teal-50',
        iconColor: 'text-teal-600',
        addButton: 'KRI',
        addLink: '/kri-library',
        viewAllLink: '/kri-library',
        stats: [
          { label: 'Total KRIs', value: kriStats.total, color: 'text-gray-900' },
          { label: 'Red', value: kriStats.red, color: 'text-red-600' },
          { label: 'Amber', value: kriStats.amber, color: 'text-yellow-600' },
          { label: 'Green', value: kriStats.green, color: 'text-green-600' }
        ]
      },
      {
        title: 'Compliance',
        icon: DocumentTextIcon,
        color: 'border-purple-200 bg-purple-50',
        iconColor: 'text-purple-600',
        addButton: 'Framework',
        addLink: '/settings/compliance',
        viewAllLink: '/compliance/regulations',
        stats: [
          { label: 'Compliance Score', value: `${complianceScore}%`, color: complianceScore >= 80 ? 'text-green-600' : complianceScore >= 60 ? 'text-yellow-600' : 'text-red-600' },
          { label: 'Frameworks', value: complianceFrameworks, color: 'text-gray-900' },
          { label: 'Requirements', value: complianceRequirements, color: 'text-gray-900' },
          { label: 'Met', value: complianceMetRequirements, color: 'text-green-600' }
        ]
      }
    ]
  ]

  const viewButtons = [
    { key: 'my', label: 'My View', icon: UserGroupIcon },
    { key: 'team', label: 'Team View', icon: UserGroupIcon },
    { key: 'all', label: 'All', icon: CogIcon }
  ]

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: Squares2X2Icon },
    { key: 'issues', label: 'Issues', icon: ExclamationTriangleIcon, count: issueStats.total },
    { key: 'incidents', label: 'Incidents', icon: ExclamationCircleIcon, count: incidentStats.total },
    { key: 'actions', label: 'Actions', icon: ClipboardDocumentListIcon, count: actionStats.total },
    { key: 'jobs', label: 'Jobs', icon: BriefcaseIcon, count: jobStats.total },
    { key: 'risks', label: 'Risks', icon: ShieldCheckIcon, count: riskStats.total },
    { key: 'controls', label: 'Controls', icon: CheckCircleIcon, count: controlStats.total },
    { key: 'compliance', label: 'Compliance', icon: DocumentTextIcon }
  ]

  // Render individual tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboardTab()
      case 'issues':
        return renderIssuesTab()
      case 'incidents':
        return renderIncidentsTab()
      case 'actions':
        return renderActionsTab()
      case 'jobs':
        return renderJobsTab()
      case 'risks':
        return renderRisksTab()
      case 'controls':
        return renderControlsTab()
      case 'compliance':
        return renderComplianceTab()
      default:
        return renderDashboardTab()
    }
  }

  const renderDashboardTab = () => (
    <>
      {/* Main Dashboard Grid - Bigger tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {tileRows.flat().map((tile) => (
          <div 
            key={tile.title} 
            className="group relative overflow-hidden rounded-2xl border border-gray-200/60 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 min-h-[320px] flex flex-col"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            {/* Header */}
            <div className="p-6 pb-4 flex-1">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-xl shadow-sm ${tile.color}`}>
                    <tile.icon className={`h-6 w-6 ${tile.iconColor}`} />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">{tile.title}</h2>
                    <p className="text-sm text-gray-600">Real-time metrics</p>
                  </div>
                </div>
                                  <Link
                    to={tile.addLink}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-all duration-200 shadow-sm hover:shadow-md min-w-[100px]"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {tile.addButton}
                  </Link>
              </div>

              {/* Stats Grid - Enhanced */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {tile.stats.map((stat, index) => (
                  <div key={index} className="bg-gray-50/50 rounded-xl p-4 text-center backdrop-blur-sm">
                    <div className={`text-lg font-bold ${stat.color} mb-1`}>
                      {stat.value}
                    </div>
                    <div className="text-xs text-gray-600 leading-tight font-medium">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* View All Link - Enhanced */}
              <Link
                to={tile.viewAllLink}
                className="flex items-center justify-center gap-2 w-full py-3 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all duration-200 border border-gray-200/60 mt-auto"
              >
                View All {tile.title}
                <ArrowTrendingUpIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </>
  )

  const renderIssuesTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Issues</h2>
          <p className="text-gray-600">Showing {issues.length} total issues</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refetchIssues}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
            disabled={issuesLoading}
          >
            <ArrowPathIcon className={`h-4 w-4 ${issuesLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link
            to="/issue-details/new"
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-all duration-200 min-w-[120px]"
          >
            <PlusIcon className="h-4 w-4" />
            New Issue
          </Link>
        </div>
      </div>
      
      {issuesLoading && (
        <div className="flex items-center justify-center py-8">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading issues...</span>
        </div>
      )}
      
      <div className="grid gap-4">
        {issues.slice(0, 50).map((issue, index) => (
          <div 
            key={issue.id || index}
            className="p-6 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-gray-600">#{issue.id || index + 1}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    (issue.fields_json?.issue_rating || issue.fields_json?.priority) === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                    (issue.fields_json?.issue_rating || issue.fields_json?.priority) === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                    (issue.fields_json?.issue_rating || issue.fields_json?.priority) === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {issue.fields_json?.issue_rating || issue.fields_json?.priority || 'LOW'}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    issue.status === 'closed' ? 'bg-green-100 text-green-800' :
                    issue.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {issue.status || 'Open'}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {issue.fields_json?.title || issue.title || `Issue ${index + 1}`}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {issue.fields_json?.description || issue.description || 'No description available'}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {issue.fields_json?.due_date && (
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="h-4 w-4" />
                      Due: {new Date(issue.fields_json.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {issue.created_at && (
                    <span>Created: {new Date(issue.created_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <Link
                to={`/issue-details/${issue.id}`}
                className="flex items-center gap-1 px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200"
              >
                <EyeIcon className="h-4 w-4" />
                View
              </Link>
            </div>
          </div>
        ))}
        
        {!issuesLoading && issues.length === 0 && (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No issues found</h3>
            <p className="text-gray-600 mb-4">
              {issuesError 
                ? `API Error: ${issuesError.message}` 
                : 'No issues available or API is not returning data. Check the console for debugging info.'}
            </p>
            <div className="text-sm text-gray-500 mb-4">
              API Endpoint: GET /api/issues
              <br />
              Status: {issuesError ? 'Error' : 'No Data'}
            </div>
            <button
              onClick={refetchIssues}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Retry Loading
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const renderIncidentsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Incidents</h2>
        <Link
          to="/incidents/new"
          className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-all duration-200 min-w-[140px]"
        >
          <PlusIcon className="h-4 w-4" />
          Report Incident
        </Link>
      </div>
      
      <div className="text-center py-12">
        <ExclamationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Incident Management</h3>
        <p className="text-gray-600">Critical and high-priority issues are tracked as incidents.</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-md mx-auto">
          <div className="p-4 bg-red-50 rounded-lg">
            <div className="text-lg font-bold text-red-600">{incidentStats.open}</div>
            <div className="text-sm text-red-700">Open</div>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg">
            <div className="text-lg font-bold text-yellow-600">{incidentStats.investigating}</div>
            <div className="text-sm text-yellow-700">Investigating</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-lg font-bold text-green-600">{incidentStats.resolved}</div>
            <div className="text-sm text-green-700">Resolved</div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderActionsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Action Plans</h2>
          <p className="text-gray-600">Showing {actions.length} total actions</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refetchActions}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
            disabled={actionsLoading}
          >
            <ArrowPathIcon className={`h-4 w-4 ${actionsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link
            to="/action-library?new=true"
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-all duration-200 min-w-[120px]"
          >
            <PlusIcon className="h-4 w-4" />
            New Action
          </Link>
        </div>
      </div>
      
      {actionsLoading && (
        <div className="flex items-center justify-center py-8">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading actions...</span>
        </div>
      )}
      
      <div className="grid gap-4">
        {actions.slice(0, 50).map((action, index) => (
          <div 
            key={action.id || index}
            className="p-6 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-gray-600">#{action.id || index + 1}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    action.status === 'completed' ? 'bg-green-100 text-green-800' :
                    action.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {action.status || 'Not Started'}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {action.fields_json?.title || action.title || `Action Plan ${index + 1}`}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {action.fields_json?.description || action.description || 'No description available'}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {action.fields_json?.due_date && (
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="h-4 w-4" />
                      Due: {new Date(action.fields_json.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {action.created_at && (
                    <span>Created: {new Date(action.created_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <Link
                to={`/action-details/${action.id}`}
                className="flex items-center gap-1 px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200"
              >
                <EyeIcon className="h-4 w-4" />
                View
              </Link>
            </div>
          </div>
        ))}
        
        {!actionsLoading && actions.length === 0 && (
          <div className="text-center py-12">
            <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No action plans found</h3>
            <p className="text-gray-600 mb-4">
              {actionsError 
                ? `API Error: ${actionsError.message}` 
                : 'No action plans available or API is not returning data. Check the console for debugging info.'}
            </p>
            <div className="text-sm text-gray-500 mb-4">
              API Endpoint: GET /api/actions
              <br />
              Status: {actionsError ? 'Error' : 'No Data'}
            </div>
            <button
              onClick={refetchActions}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Retry Loading
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const renderJobsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Jobs</h2>
          <p className="text-gray-600">Showing {jobs.length} total jobs</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refetchJobs}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
            disabled={jobsLoading}
          >
            <ArrowPathIcon className={`h-4 w-4 ${jobsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link
            to="/jobs/new"
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-all duration-200 min-w-[120px]"
          >
            <PlusIcon className="h-4 w-4" />
            New Job
          </Link>
        </div>
      </div>
      
      {jobsLoading && (
        <div className="flex items-center justify-center py-8">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading jobs...</span>
        </div>
      )}
      
      <div className="grid gap-4">
        {jobs.slice(0, 50).map((job, index) => (
          <div 
            key={job.id || index}
            className="p-6 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-gray-600">#{job.id || index + 1}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    job.status === 'done' ? 'bg-green-100 text-green-800' :
                    job.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    job.status === 'todo' ? 'bg-gray-100 text-gray-800' :
                    job.status === 'failed' ? 'bg-red-100 text-red-800' :
                    job.status === 'skipped' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {job.status === 'done' ? 'Completed' :
                     job.status === 'in_progress' ? 'In Progress' :
                     job.status === 'todo' ? 'To Do' :
                     job.status === 'failed' ? 'Failed' :
                     job.status === 'skipped' ? 'Skipped' :
                     'Unknown'}
                  </span>
                  {job.linked_entity_type && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {job.linked_entity_type}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {job.title || `Job ${index + 1}`}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {job.description || 'No description available'}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {job.due_date && (
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="h-4 w-4" />
                      Due: {new Date(job.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {job.owner_user_id && (
                    <span className="flex items-center gap-1">
                      <UserGroupIcon className="h-4 w-4" />
                      Owner: {job.owner_user_id}
                    </span>
                  )}
                  {job.type && (
                    <span>Type: {job.type}</span>
                  )}
                  {job.created_at && (
                    <span>Created: {new Date(job.created_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <Link
                to={`/jobs/${job.id}`}
                className="flex items-center gap-1 px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200"
              >
                <EyeIcon className="h-4 w-4" />
                View
              </Link>
            </div>
          </div>
        ))}
        
        {!jobsLoading && jobs.length === 0 && (
          <div className="text-center py-12">
            <BriefcaseIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
            <p className="text-gray-600 mb-4">
              {jobsError 
                ? `API Error: ${jobsError.message}` 
                : 'No jobs available or API is not returning data. Check the console for debugging info.'}
            </p>
            <div className="text-sm text-gray-500 mb-4">
              API Endpoint: GET /api/jobs
              <br />
              Status: {jobsError ? 'Error' : 'No Data'}
            </div>
            <button
              onClick={refetchJobs}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Retry Loading
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const renderRisksTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Risk Register</h2>
          <p className="text-gray-600">Showing {risks.length} total risks</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refetchRisks}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
            disabled={risksLoading}
          >
            <ArrowPathIcon className={`h-4 w-4 ${risksLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link
            to="/risk-details/new"
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-all duration-200 min-w-[110px]"
          >
            <PlusIcon className="h-4 w-4" />
            New Risk
          </Link>
        </div>
      </div>
      
      {risksLoading && (
        <div className="flex items-center justify-center py-8">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading risks...</span>
        </div>
      )}
      
      <div className="grid gap-4">
        {risks.slice(0, 50).map((risk, index) => (
          <div 
            key={risk.id || index}
            className="p-6 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-gray-600">#{risk.id || index + 1}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    (risk.fields_json?.rating || risk.json_fields?.rating) === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                    (risk.fields_json?.rating || risk.json_fields?.rating) === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                    (risk.fields_json?.rating || risk.json_fields?.rating) === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {risk.fields_json?.rating || risk.json_fields?.rating || 'LOW'}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {risk.name || risk.title || `Risk ${index + 1}`}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {risk.fields_json?.description || risk.description || 'No description available'}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {risk.category && (
                    <span>Category: {risk.category}</span>
                  )}
                  {risk.created_at && (
                    <span>Created: {new Date(risk.created_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <Link
                to={`/risk-details/${risk.id}`}
                className="flex items-center gap-1 px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200"
              >
                <EyeIcon className="h-4 w-4" />
                View
              </Link>
            </div>
          </div>
        ))}
        
        {!risksLoading && risks.length === 0 && (
          <div className="text-center py-12">
            <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No risks found</h3>
            <p className="text-gray-600 mb-4">Start building your risk register by adding your first risk.</p>
            <button
              onClick={refetchRisks}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Retry Loading
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const renderControlsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Controls Library</h2>
          <p className="text-gray-600">Showing {controls.length} total controls</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refetchControls}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
            disabled={controlsLoading}
          >
            <ArrowPathIcon className={`h-4 w-4 ${controlsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link
            to="/control-details/new"
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-all duration-200 min-w-[130px]"
          >
            <PlusIcon className="h-4 w-4" />
            New Control
          </Link>
        </div>
      </div>
      
      {controlsLoading && (
        <div className="flex items-center justify-center py-8">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading controls...</span>
        </div>
      )}
      
      <div className="grid gap-4">
        {controls.slice(0, 50).map((control, index) => (
          <div 
            key={control.id || index}
            className="p-6 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-gray-600">#{control.id || index + 1}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    control.fields_json?.effectiveness === 'Effective' ? 'bg-green-100 text-green-800' :
                    control.fields_json?.effectiveness === 'Needs Review' ? 'bg-yellow-100 text-yellow-800' :
                    control.fields_json?.effectiveness === 'Partially Effective' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {control.fields_json?.effectiveness || 'Not Assessed'}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {control.name || control.title || `Control ${index + 1}`}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {control.fields_json?.description || control.description || 'No description available'}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {control.fields_json?.due_date && (
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="h-4 w-4" />
                      Due: {new Date(control.fields_json.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {control.created_at && (
                    <span>Created: {new Date(control.created_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <Link
                to={`/control-details/${control.id}`}
                className="flex items-center gap-1 px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200"
              >
                <EyeIcon className="h-4 w-4" />
                View
              </Link>
            </div>
          </div>
        ))}
        
        {!controlsLoading && controls.length === 0 && (
          <div className="text-center py-12">
            <CheckCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No controls found</h3>
            <p className="text-gray-600 mb-4">Build your controls library by adding your first control.</p>
            <button
              onClick={refetchControls}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Retry Loading
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const renderComplianceTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Compliance Overview</h2>
        <Link
          to="/settings/compliance"
          className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-all duration-200 min-w-[180px]"
        >
          <CogIcon className="h-4 w-4" />
          Manage Frameworks
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          className="p-6 rounded-xl border border-gray-200/60 shadow-sm"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div className="text-center">
            <div className={`text-4xl font-bold mb-2 ${complianceScore >= 80 ? 'text-green-600' : complianceScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
              {complianceScore}%
            </div>
            <div className="text-gray-600 font-medium">Overall Compliance Score</div>
          </div>
        </div>
        
        <div 
          className="p-6 rounded-xl border border-gray-200/60 shadow-sm"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div className="text-center">
            <div className="text-4xl font-bold mb-2 text-gray-900">{complianceFrameworks}</div>
            <div className="text-gray-600 font-medium">Active Frameworks</div>
          </div>
        </div>
        
        <div 
          className="p-6 rounded-xl border border-gray-200/60 shadow-sm"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div className="text-center">
            <div className="text-4xl font-bold mb-2 text-green-600">{complianceMetRequirements}</div>
            <div className="text-gray-600 font-medium">Requirements Met</div>
            <div className="text-sm text-gray-500 mt-1">of {complianceRequirements} total</div>
          </div>
        </div>
      </div>
      
      <div className="text-center py-8">
        <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Compliance Management</h3>
        <p className="text-gray-600">Track your compliance with regulatory frameworks and standards.</p>
      </div>
    </div>
  )

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
      {[...Array(6)].map((_, index) => (
        <div 
          key={index}
          className="rounded-2xl border border-gray-200/60 shadow-sm p-4 animate-pulse min-h-[280px]"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gray-200 rounded-xl"></div>
              <div>
                <div className="h-3 bg-gray-200 rounded w-16 mb-1"></div>
                <div className="h-2 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
            <div className="h-6 w-12 bg-gray-200 rounded-lg"></div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="h-4 bg-gray-200 rounded w-6 mx-auto mb-1"></div>
                <div className="h-2 bg-gray-200 rounded w-12 mx-auto"></div>
              </div>
            ))}
          </div>
          <div className="h-6 bg-gray-200 rounded-lg mt-auto"></div>
        </div>
      ))}
    </div>
  )

  if (summaryLoading) {
    return (
      <div 
        className="h-full overflow-auto"
        style={{
          background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 25%, #f8fafc 50%, #f1f4f8 75%, #f9fafb 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 20s ease infinite'
        }}
      >
        <style>
          {`
            @keyframes gradientShift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}
        </style>
        
        <div className="px-8 py-10 w-full max-w-none">
          {/* Header Skeleton */}
          <div 
            className="flex items-center justify-between mb-6 p-4 rounded-2xl border border-gray-200/60 shadow-sm animate-pulse"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div>
              <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-48"></div>
            </div>
            <div className="flex items-center gap-4">
              <div className="h-8 w-56 bg-gray-200 rounded-xl"></div>
              <div className="h-3 w-28 bg-gray-200 rounded"></div>
            </div>
          </div>

          {/* Tab Navigation Skeleton */}
          <div className="mb-6">
            <div 
              className="flex space-x-1 p-1 rounded-2xl border border-gray-200/60 shadow-sm animate-pulse"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
                backdropFilter: 'blur(20px)',
              }}
            >
              {[...Array(7)].map((_, index) => (
                <div key={index} className="h-10 bg-gray-200 rounded-xl w-24"></div>
              ))}
            </div>
          </div>
          
          <LoadingSkeleton />
        </div>
      </div>
    )
  }

  return (
    <div 
      className="h-full overflow-auto"
      style={{
        background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 25%, #f8fafc 50%, #f1f4f8 75%, #f9fafb 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 20s ease infinite'
      }}
    >
      <style>
        {`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
      
      <div className="px-4 py-6 w-full max-w-none">
        {/* Header */}
        <div 
          className="flex items-center justify-between mb-6 p-4 rounded-2xl border border-gray-200/60 shadow-sm"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div>
            <h1 className="text-lg font-bold text-gray-900 mb-2">Welcome Mohammed Khan!</h1>
          </div>
          
          {/* View Filter Buttons */}
          <div className="flex items-center gap-4">
            <button
              onClick={refreshAllData}
              className="flex items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200/60 rounded-lg hover:bg-white/90 transition-all duration-200"
              disabled={summaryLoading}
            >
              <ArrowPathIcon className={`h-4 w-4 ${summaryLoading ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
            <div className="flex bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200/60 p-1">
              {viewButtons.map((button) => (
                <button
                  key={button.key}
                  onClick={() => setActiveView(button.key)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    activeView === button.key
                      ? 'bg-gray-900 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <button.icon className="h-4 w-4" />
                  {button.label}
                </button>
              ))}
            </div>
            <div className="text-sm text-gray-500">
              Last updated: {new Date().toLocaleString()}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div 
            className="flex space-x-1 p-1 rounded-2xl border border-gray-200/60 shadow-sm overflow-x-auto"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-gray-900 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    activeTab === tab.key
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tab Content */}
        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  )
}

export default Dashboard 